import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBanknote, LuBriefcase, LuBuilding2, LuCalendarDays, LuClock, LuFingerprint, LuMail, LuPhone, LuReceipt, LuWalletCards,
} from 'react-icons/lu';
import DetailPane from '../../../../components/data/DetailPane';
import useEmployeeFinanceText from '../employeeFinanceText';
import { ActiveBadge, STATUS_GRADIENT, initialsOf, isBlank, useFieldText } from './EmployeeFinanceCell';

/**
 * The selected employee: Maaş, Ödemeler and Çalışan Bütçe Ücretlendirme
 * (the old action bar), plus contact and employment details. Docked beside
 * the table on wide screens, a drawer otherwise.
 */
const EmployeeFinanceDetailPane = ({ docked, height, employee, position, onPrev, onNext, onClose, onSalary, onPayments, onBudgetPricing }) => {
  const { label } = useEmployeeFinanceText();
  const fieldText = useFieldText();
  const paneLabel = label('panel.label');

  if (!employee) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const name = `${employee.eName || ''} ${employee.eSurname || ''}`.trim() || '-';
  const text = (key) => fieldText(employee, key) || '-';

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={employee.id}
      identity={{
        avatar: initialsOf(employee),
        gradient: employee.Active === 1 ? STATUS_GRADIENT.active : STATUS_GRADIENT.inactive,
        title: name,
        subtitle: isBlank(employee.departman) ? undefined : String(employee.departman),
        badges: <ActiveBadge value={employee.Active} />,
      }}
      actions={[
        { key: 'salary', label: label('panel.salary'), icon: LuBanknote, tint: 'emerald', onClick: onSalary },
        { key: 'payments', label: label('panel.payments'), icon: LuReceipt, tint: 'blue', onClick: onPayments },
        { key: 'budget', label: label('panel.budgetPricing'), icon: LuWalletCards, tint: 'violet', onClick: onBudgetPricing },
      ]}
      details={[
        { key: 'tc', icon: LuFingerprint, label: label('columns.tcNumber'), value: text('tcNumber'), copyable: !isBlank(employee.tcNumber) },
        { key: 'phone', icon: LuPhone, label: label('columns.phone'), value: text('phone'), href: isBlank(employee.phone) ? undefined : `tel:${employee.phone}` },
        { key: 'email', icon: LuMail, label: label('columns.eMail'), value: text('eMail'), href: isBlank(employee.eMail) ? undefined : `mailto:${employee.eMail}`, copyable: !isBlank(employee.eMail) },
        { key: 'department', icon: LuBuilding2, label: label('columns.departman'), value: text('departman') },
        { key: 'start', icon: LuCalendarDays, label: label('columns.startDate'), value: text('startDate') },
        { key: 'work', icon: LuClock, label: label('columns.workType'), value: text('workType') },
        { key: 'profession', icon: LuBriefcase, label: label('columns.profession'), value: text('profession') },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

EmployeeFinanceDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  employee: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSalary: PropTypes.func.isRequired,
  onPayments: PropTypes.func.isRequired,
  onBudgetPricing: PropTypes.func.isRequired,
};

export default EmployeeFinanceDetailPane;
